import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { SettingsService } from '@delon/theme';
import { NzDatePickerComponent } from 'ng-zorro-antd/date-picker';
import { Subscription } from 'rxjs';
import { DateFormatService } from 'src/app/shared/services/date-format.service';
import { MomentService } from 'src/app/shared/services/moment.service';

@Component({
  selector: 'app-date-input',
  templateUrl: './date-input.component.html',
  styles: [],
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateInputComponent),
      multi: true
    }
  ]
})
export class DateInputComponent
  implements ControlValueAccessor, OnInit, AfterViewInit, OnDestroy
{
  @Input() value: any;
  @Input() disabled = false;
  @Input() _disabled = false; // Passing by parent component
  @Input() nzDisabled = false;
  @Input() maxLength: string | number | null = null;
  @Input() borderless = false;
  @Input() nzAllowClear = false;
  @Input() tabindex: string | null = null;
  @ViewChild('datepicker') datepicker!: NzDatePickerComponent;
  date: Date | null = null;
  format = 'MM/dd/yyyy';

  get readonly() {
    if (this.disabled || this._disabled) {
      return true;
    }
    return false;
  }

  onChange: any = () => {};
  onTouched: any = () => {};
  private dateFormatSub?: Subscription;

  constructor(
    private settings: SettingsService,
    private dateSrv: MomentService,
    private cdr: ChangeDetectorRef,
    private dateFormatService: DateFormatService
  ) {
    this.format =
      this.dateFormatService.currentDateFormatValue ||
      this.dateFormatService.getDefaultDateFormat();
  }

  ngOnInit(): void {
    this.dateFormatSub = this.dateFormatService.currentDateFormat.subscribe(
      format => {
        this.format = format || this.dateFormatService.getDefaultDateFormat();
      }
    );
  }
  ngOnDestroy(): void {
    this.dateFormatSub?.unsubscribe();
  }
  ngAfterViewInit(): void {
    if (this.tabindex && this.datepicker) {
      const nzDatePicker = this.datepicker.origin.nativeElement; // console.log('nzSelect Element:', nzSelectElement);  // Log the element to see the structure

      const searchInput = nzDatePicker.querySelector('input');

      if (searchInput) {
        searchInput.setAttribute('tabindex', this.tabindex?.toString() || '0');
      }
    }
  }
  writeValue(value: any): void {
    if (value) {
      this.date = this.dateSrv.toDateStart(value);
    } else {
      this.date = null;
    }
    this.value = this.formatDate();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
  onModelChange(val: any) {
    if (val) {
      this.date = this.dateSrv.toDateStart(val);
    } else this.date = null;
    this.value = this.formatDate();
    this.onChange(this.value);
    this.cdr.markForCheck();
  }

  formatDate() {
    if (this.date) {
      const year = this.date.getFullYear();
      const month = this.date.getMonth() + 1;
      const day = this.date.getDate();
      const format = `${year}-${month}-${day}`;
      return format;
    }
    return null;
  }

  onKeyUp(event: any) {
    const value = (event.target as HTMLInputElement).value;
    const newDate = this.parseCompactDateInput(value);
    if (newDate) {
      this.date = newDate;
      this.onModelChange(this.date);
    }
  }

  private parseCompactDateInput(value: string): Date | null {
    if (!/^\d+$/.test(value)) return null;

    const tokens = this.format
      .split(/[\/\-.]/)
      .map(t => t.trim().toLowerCase())
      .filter(Boolean);
    if (tokens.length !== 3) return null;
    if (tokens.some(t => !['dd', 'mm', 'yy', 'yyyy'].includes(t))) return null;

    const lengths = tokens.map(t => (t === 'yyyy' ? 4 : 2));
    const expectedLength = lengths.reduce((sum, len) => sum + len, 0);
    if (value.length !== expectedLength) return null;

    let cursor = 0;
    const parts = new Map<string, number>();
    for (let i = 0; i < tokens.length; i++) {
      const token = tokens[i];
      const length = lengths[i];
      const part = Number(value.slice(cursor, cursor + length));
      if (Number.isNaN(part)) return null;
      parts.set(token, part);
      cursor += length;
    }

    const month = parts.get('mm');
    const day = parts.get('dd');
    const year = parts.has('yyyy')
      ? parts.get('yyyy')
      : 2000 + (parts.get('yy') ?? 0);
    if (!year || !month || !day) return null;

    const parsed = new Date(year, month - 1, day);
    if (
      parsed.getFullYear() !== year ||
      parsed.getMonth() !== month - 1 ||
      parsed.getDate() !== day
    ) {
      return null;
    }

    return parsed;
  }
}
